import React from 'react';
import {useIntl} from 'react-intl';
import {FlexColumn, FlexRow, Separator, Text, TextArea, TextField, Toggle} from '@forecast-it/design-system';
import {Card} from './Card';
import {DESCRIPTION_MAX_LENGTH, TemplateDraft} from './baselineTemplateDraft';

type Props = {
	draft: TemplateDraft;
	fieldError: (path: string) => string | undefined;
	onChange: (patch: Partial<TemplateDraft>, changedPath?: string) => void;
	onNameBlur: () => void;
};

export const TemplateDetailsCard = ({draft, fieldError, onChange, onNameBlur}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	return (
		<Card data-cy="baseline-template-details">
			<div data-field-path="name">
				<TextField
					label={formatMessage({id: 'baseline_templates.details.name'})}
					data-cy="baseline-template-name"
					placeholder={formatMessage({id: 'baseline_templates.details.name_placeholder'})}
					value={draft.name}
					onValueChange={(value: string) => onChange({name: value}, 'name')}
					onBlur={onNameBlur}
					hasError={!!fieldError('name')}
					errorText={fieldError('name')}
				/>
			</div>
			{/* TextArea hands onChange the value, not an event, and needs an onBlur even though every
			    change is already kept. */}
			<TextArea
				label={formatMessage({id: 'baseline_templates.details.description'})}
				data-cy="baseline-template-description"
				placeholder={formatMessage({id: 'baseline_templates.details.description_placeholder'})}
				isOptional
				height={72}
				maxLength={DESCRIPTION_MAX_LENGTH}
				value={draft.description}
				onChange={(value: string) => onChange({description: value})}
				onBlur={() => undefined}
			/>
			<Separator />
			<FlexRow justifyContent="space-between" alignItems="flex-start" gap="l">
				<FlexColumn gap="xs">
					<Text>{formatMessage({id: 'baseline_templates.details.include_deliverables'})}</Text>
					<Text color="auxiliary">
						{formatMessage({id: 'baseline_templates.details.include_deliverables_helper'})}
					</Text>
				</FlexColumn>
				<Toggle
					checked={draft.includeDeliverables}
					onChange={() => onChange({includeDeliverables: !draft.includeDeliverables})}
					cy="baseline-template-include-deliverables"
				/>
			</FlexRow>
		</Card>
	);
};
